import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { UserPlus, Trash2, Loader2, ShieldCheck, Eye, Crown } from "lucide-react";

export default function AllowedUsers() {
  const [rows, setRows] = useState([]);
  const [owners, setOwners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("viewer");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/v1/admin/allowed-users");
      setRows(r.data.users);
      setOwners(r.data.owner_emails || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      await api.post("/v1/admin/allowed-users", { email: email.trim(), role });
      setEmail(""); setRole("viewer");
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not add user.");
    } finally { setBusy(false); }
  };

  const remove = async (em) => {
    setBusy(true);
    try {
      await api.delete(`/v1/admin/allowed-users/${encodeURIComponent(em)}`);
      await load();
    } finally { setBusy(false); }
  };

  const inputCls = "rounded-lg border border-slate-700 bg-slate-900/70 px-3 py-2.5 text-sm text-slate-100 placeholder:text-slate-600 focus:border-blue-500 focus:outline-none transition-colors duration-150";

  return (
    <div>
      <PageHeader
        eyebrow="Admin · access control"
        title="Allowed Users"
        subtitle="Only emails on this list can sign in — via Google or email code. Removing a user logs them out immediately."
      />

      <form onSubmit={add} className="mb-8 flex flex-wrap items-end gap-3 rounded-2xl border border-slate-800 bg-slate-900/60 p-5 max-w-3xl">
        <div className="flex-1 min-w-[220px]">
          <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wide text-slate-500">Email</label>
          <input
            type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="person@organisation.org" data-testid="allow-email-input"
            className={`${inputCls} w-full`}
          />
        </div>
        <div className="w-36">
          <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wide text-slate-500">Role</label>
          <select value={role} onChange={(e) => setRole(e.target.value)} data-testid="allow-role-select" className={`${inputCls} w-full`}>
            <option value="viewer">Viewer</option>
            <option value="admin">Admin</option>
          </select>
        </div>
        <button
          type="submit" disabled={busy} data-testid="allow-add-btn"
          className="inline-flex items-center gap-2 rounded-full bg-[#8a1420] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#6f101a] disabled:opacity-50 transition-colors duration-150"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />} Add user
        </button>
        {error && <div data-testid="allow-error" className="w-full text-sm text-rose-400">{error}</div>}
      </form>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-slate-400"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : (
        <div className="space-y-2 max-w-3xl">
          {owners.map((o) => (
            <div key={o} data-testid={`owner-row-${o}`} className="flex items-center justify-between gap-3 rounded-xl border border-amber-400/30 bg-amber-400/5 px-4 py-3">
              <div className="flex items-center gap-2.5 min-w-0">
                <Crown className="w-4 h-4 text-amber-400 shrink-0" />
                <span className="truncate text-sm text-slate-200">{o}</span>
                <span className="rounded-full border border-amber-400/40 px-2 py-0.5 text-[10px] font-semibold uppercase text-amber-300">Owner · Admin</span>
              </div>
              <span className="text-[11px] text-slate-500">always allowed</span>
            </div>
          ))}
          {rows.length === 0 ? (
            <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-6 text-center text-sm text-slate-500">
              No additional users yet. Add someone above to grant access.
            </div>
          ) : rows.map((u) => (
            <div key={u.email} data-testid={`allowed-row-${u.email}`} className="flex items-center justify-between gap-3 rounded-xl border border-slate-800 bg-slate-900/60 px-4 py-3">
              <div className="flex items-center gap-2.5 min-w-0">
                {u.role === "admin"
                  ? <ShieldCheck className="w-4 h-4 text-emerald-400 shrink-0" />
                  : <Eye className="w-4 h-4 text-slate-400 shrink-0" />}
                <span className="truncate text-sm text-slate-200">{u.email}</span>
                <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase ${u.role === "admin" ? "border-emerald-400/40 text-emerald-300" : "border-slate-600 text-slate-400"}`}>{u.role}</span>
              </div>
              <button
                onClick={() => remove(u.email)} disabled={busy}
                data-testid={`allowed-remove-${u.email}`}
                className="inline-flex items-center gap-1 rounded-full border border-rose-400/40 px-3 py-1 text-xs font-semibold text-rose-400 hover:bg-rose-400/10 disabled:opacity-50 transition-colors duration-150"
              >
                <Trash2 className="w-3.5 h-3.5" /> Remove
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
